import React from 'react';
import { Server, Activity, Cpu, HardDrive, Clock } from 'lucide-react';
import { formatBytes } from './utils';

const ProgressBar = ({ value, color }) => (
  <div className="w-full bg-gray-200 rounded-full h-2">
    <div
      className={`h-2 rounded-full transition-all ${color}`}
      style={{ width: `${value}%` }}
    />
  </div>
);

const cpuBarColor = (load) => (load > 80 ? 'bg-red-500' : load > 60 ? 'bg-orange-500' : 'bg-green-500');
const memBarColor = (pct) => (pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-orange-500' : 'bg-blue-500');
const diskBarColor = (pct) => (pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-orange-500' : 'bg-purple-500');

/**
 * Grid 2-kolom: System Information + Resource Usage.
 * Hilang otomatis saat router offline atau systemResources kosong.
 */
export const MPanelSystemInfo = ({ visible, systemResources }) => {
  if (!visible) return null;
  return (
    <div data-testid="mpanel-sysinfo" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* System Information */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">System Information</h2>
        </div>
        {systemResources && (
          <div className="space-y-3">
            <Row label="Router Version" value={systemResources.version} />
            <Row label="Board Name" value={systemResources.board_name} />
            <Row label="Architecture" value={systemResources.architecture} />
            <Row label="CPU Count" value={`${systemResources.cpu_count} cores @ ${systemResources.cpu_frequency} MHz`} />
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Uptime
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{systemResources.uptime}</span>
            </div>
          </div>
        )}
      </div>

      {/* Resource Usage */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Resource Usage</h2>
        </div>
        {systemResources && (
          <div className="space-y-4">
            {/* CPU */}
            <ResourceBar
              icon={<Cpu className="w-4 h-4" />}
              label="CPU Load"
              valueLabel={`${systemResources.cpu_load}%`}
              barValue={systemResources.cpu_load}
              barColor={cpuBarColor(systemResources.cpu_load)}
            />
            {/* Memory */}
            <ResourceBar
              icon={<Activity className="w-4 h-4" />}
              label="Memory"
              valueLabel={
                <>
                  {formatBytes(systemResources.memory_used)} / {formatBytes(systemResources.memory_total)}
                  <span className="text-gray-500 ml-1">({systemResources.memory_usage_percent}%)</span>
                </>
              }
              barValue={systemResources.memory_usage_percent}
              barColor={memBarColor(systemResources.memory_usage_percent)}
            />
            {/* Disk */}
            <ResourceBar
              icon={<HardDrive className="w-4 h-4" />}
              label="Disk Space"
              valueLabel={
                <>
                  {formatBytes(systemResources.disk_used)} / {formatBytes(systemResources.disk_total)}
                  <span className="text-gray-500 ml-1">({systemResources.disk_usage_percent}%)</span>
                </>
              }
              barValue={systemResources.disk_usage_percent}
              barColor={diskBarColor(systemResources.disk_usage_percent)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
    <span className="text-sm font-medium text-gray-900 dark:text-white">{value}</span>
  </div>
);

const ResourceBar = ({ icon, label, valueLabel, barValue, barColor }) => (
  <div>
    <div className="flex justify-between items-center mb-1">
      <span className="text-sm text-gray-600 flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{valueLabel}</span>
    </div>
    <ProgressBar value={barValue} color={barColor} />
  </div>
);
